if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // When Rebuy Smart Cart is active, the theme posts a plain add and Rebuy's
      // drawer picks it up. Rebuy only posts its own add if ours is late, so
      // handleRebuySubmit must post first.
      if (theme.settings.useRebuyCart) {
        this.handleRebuySubmit();
        return;
      }

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let succeeded = false;
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        // An error page (429, 5xx) may not be JSON.
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // A 422 may still have added what was available ("Only 3 items were added...").
          this.setErrorMsgState(ProductForm.getErrorMessage(data) || theme.strings.cartError);
          await ProductForm.refreshCartIcon().catch(() => {});
          throw new Error(response.status);
        }
        succeeded = true;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          if (!ProductForm.updateCartIcon(data)) ProductForm.refreshCartIcon().catch(() => {});

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (!succeeded && (!this.errorMsg || this.errorMsg.hidden)) {
          this.setErrorMsgState(theme.strings.cartError);
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        if (succeeded) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Handles add-to-cart when Rebuy Smart Cart is active: posts the add,
     * updates the cart icon and shows any error. Rebuy's drawer syncs itself.
     */
    async handleRebuySubmit() {
      let succeeded = false;
      try {
        // Post straight away: if this request is delayed, Rebuy assumes the
        // native add never happened and adds the item itself without the
        // X-Requested-With header, which skips Shopify's stock check.
        const formData = new FormData(this.form);
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', 'cart-icon-bubble');

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        // An error page (429, 5xx) may not be JSON.
        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          // The add succeeded: nothing after this point may report it as a failure.
          succeeded = true;
          if (!ProductForm.updateCartIcon(data)) await ProductForm.refreshCartIcon();

          const cartResponse = await fetch(`${theme.routes.cart}.js`);
          if (cartResponse.ok) {
            const cartData = await cartResponse.json();
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: { cart: cartData }
            }));
          }
        } else {
          // Shopify returns 422 when the quantity exceeds stock, but may still
          // have added what was available ("Only 3 items were added..."). Show
          // the message and refresh the cart count. Rebuy's drawer syncs itself.
          this.setErrorMsgState(ProductForm.getErrorMessage(data) || theme.strings.cartError);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent }
          }));
          await ProductForm.refreshCartIcon();
        }
      } catch (error) {
        console.error('Add to cart failed:', error); // eslint-disable-line
        if (!succeeded && (!this.errorMsg || this.errorMsg.hidden)) {
          this.setErrorMsgState(theme.strings.cartError);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent }
          }));
        }
      } finally {
        // Re-enable 'Add to Cart' button.
        if (succeeded) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     * @returns {boolean} Whether the response carried the section to update it with.
     */
    static updateCartIcon(response) {
      const html = response && response.sections && response.sections['cart-icon-bubble'];
      if (!html) return false;
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) cartIconBubble.innerHTML = html;
      return true;
    }

    /**
     * Re-renders the cart icon count when the add response carried no sections
     * (an error response, which may still have added part of the quantity).
     */
    static async refreshCartIcon() {
      const response = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
      if (!response.ok) return;
      const sections = await response.json();
      ProductForm.updateCartIcon({ sections });
    }

    /**
     * Gets the message to show from a /cart/add error response.
     * @param {object} data - Response JSON.
     * @returns {string|string[]|undefined}
     */
    static getErrorMessage(data) {
      if (data.errors && typeof data.errors === 'object') {
        return Object.entries(data.errors).map((item) => [].concat(item[1]).join(', '));
      }
      return typeof data.description === 'string' ? data.description : data.message;
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
